import { useCallback, useEffect, useState } from 'react';
import { Grid, Paper, Box, ListItem, ListItemIcon, ListItemText, Typography, IconButton } from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import ArrowBackButton from '@material-ui/icons/ArrowBack';
import { Link, useParams } from 'react-router-dom';
import SummaryCard from './SummaryCard';
import MatchHistoryCard from './MatchHistoryCard';
import VerdictCard from './VerdictCard';
import DifficultyCard from './DifficultyCard';
import PaceCard from './PaceCard';
import GridCard from './GridCard';
import TagsCard from './TagsCard';

// The statistics of one contestant of one tournament, laid out as a dashboard:
// the headline numbers first, then what they played, then what their solves and
// their submissions were made of.
function ParticipantStatistics() {
    const { tournamentId, username } = useParams();
    const [statistics, setStatistics] = useState(null);
    const [errorMessages, setErrorMessages] = useState(null);

    const getStatistics = useCallback(async () => {
        const response = await fetch(
            `/api/tournament/${tournamentId}/participant/${encodeURIComponent(username)}`,
            { method: 'GET', credentials: 'include' }
        );
        const result = await response.json();
        if (result.status !== 'OK') {
            setStatistics(null);
            setErrorMessages(result.body?.errorMessages ?? ['Statistics could not be loaded']);
            return;
        }
        setErrorMessages(null);
        setStatistics(result.body);
    }, [tournamentId, username]);

    useEffect(() => {
        getStatistics();
    }, [getStatistics]);

    if (errorMessages) {
        return (
            <Paper elevation={6}>
                <Box p={2}>
                    <Typography variant='h6' gutterBottom> Error </Typography>
                    {errorMessages.map(message => <Typography key={message}> {message} </Typography>)}
                </Box>
            </Paper>
        );
    }

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Paper elevation={6}>
                    <ListItem>
                        <ListItemIcon> <PersonIcon fontSize='large' /> </ListItemIcon>
                        <ListItemText
                            disableTypography
                            primary={<Typography variant='h5'> {username} </Typography>}
                            secondary={<Typography variant='body2' color='textSecondary'> Tournament statistics </Typography>}
                        />
                        <IconButton component={Link} to={`/tournament/${tournamentId}`}>
                            <ArrowBackButton />
                        </IconButton>
                    </ListItem>
                </Paper>
            </Grid>
            {statistics == null ?
                <Grid item xs={12}>
                    <Paper elevation={6}>
                        <Box p={2}> <Typography color='textSecondary'> Loading statistics… </Typography> </Box>
                    </Paper>
                </Grid> :
                <>
                    <Grid item xs={12}>
                        <SummaryCard summary={statistics.summary} verdicts={statistics.verdicts} />
                    </Grid>
                    <Grid item xs={12} lg={8}>
                        <MatchHistoryCard matches={statistics.matches} />
                    </Grid>
                    <Grid item xs={12} lg={4}>
                        <GridCard grid={statistics.grid} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <VerdictCard verdicts={statistics.verdicts} submissions={statistics.summary.submissions} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <DifficultyCard difficulty={statistics.difficulty} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <PaceCard pace={statistics.pace} />
                    </Grid>
                    {/* both orders of the tag columns side by side, so the card takes the row */}
                    <Grid item xs={12}>
                        <TagsCard tags={statistics.tags} taggedSolves={statistics.taggedSolves} />
                    </Grid>
                </>
            }
        </Grid>
    );
}

export default ParticipantStatistics;
